import { FormControl, InputLabel, Select, selectClasses } from '@mui/material';
import { VyTooltip } from '../../new-design/VyTooltip';
import { getSelectorAttribute, vyTableSelectors } from '@vy/app-selectors';
import type { InvoiceDate } from '@vy/types';
import type { Nullable, ValueOf } from '@vy/utils';
import { vyDate } from '@vy/utils';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { MONTHS_LABELS } from '../../../pages/customers/OldCustomerOverviewPage/customer-view-page-components/BillingScheduleChart/billing-schedule-chart-consts';
import { ArrowIcon } from '../../icons/ArrowIcon';
import { ARROW_ICON_DIRECTIONS } from '../../icons/ArrowIcon/types/arrow-icon-direction';
import { SelectedIcon } from '../../icons/SelectedIcon';
import {
  formatSelectedDatesDisplayWithMonthString,
  formatSelectedDatesTooltipWithMonthString,
} from '../../../pages/invoices/InvoicesPage/invoices-page-utils/date-display-utils';

type CalendarDropdownProps = {
  onChange?: (dates: InvoiceDate[], year: number) => void;
  defaultSelectedMonths: { month: string; year: number }[];
  defaultSelectedYear: Nullable<number>;
};

function isDateSelected(month: string, year: number, dates: InvoiceDate[]): boolean {
  return dates.some((date) => date.month === month && date.year === year);
}

export const CalendarDropdown: React.FC<CalendarDropdownProps> = ({
  onChange,
  defaultSelectedMonths,
  defaultSelectedYear,
}) => {
  const [selectedYear, setSelectedYear] = useState<number>(defaultSelectedYear ?? vyDate().year());
  const [selectedDates, setSelectedDates] = useState<InvoiceDate[]>(defaultSelectedMonths ?? []);

  const handleMonthSelect = useCallback(
    (selectedMonth: string) => {
      setSelectedDates((previousDate) => {
        const updatedDates = isDateSelected(selectedMonth, selectedYear, previousDate)
          ? previousDate.filter((date) => !(date.year === selectedYear && date.month === selectedMonth))
          : [...previousDate, { month: selectedMonth, year: selectedYear }];

        onChange?.(updatedDates, selectedYear);

        return updatedDates;
      });
    },
    [onChange, selectedYear],
  );

  const selectEntireYear = () => {
    const isFullYearAlreadySelected = MONTHS_LABELS.every((month) =>
      isDateSelected(month, selectedYear, selectedDates),
    );

    if (isFullYearAlreadySelected) {
      const remainingDates = selectedDates.filter((date) => date.year !== selectedYear);

      setSelectedDates(remainingDates);
      onChange?.(remainingDates, selectedYear);

      return;
    }

    const datesFromOtherYears = selectedDates.filter((date) => date.year !== selectedYear);
    const data = MONTHS_LABELS.map((month) => ({ month, year: selectedYear }));

    const payload = [...datesFromOtherYears, ...data];

    setSelectedDates(payload);

    onChange?.(payload, selectedYear);
  };

  const handleYearChange = (action: YearChangeType) => {
    setSelectedYear((previousYear) => {
      return action === YEAR_CHANGE_TYPES.INCREMENT ? previousYear + 1 : previousYear - 1;
    });
  };

  return (
    <FormControl
      sx={{
        height: '100%',
      }}
    >
      <InputLabel
        sx={{
          position: 'unset',
          transform: 'unset',
          fontSize: '12px',
        }}
      >
        Issue Date
      </InputLabel>

      <Select
        {...getSelectorAttribute(vyTableSelectors.dateRangePicker)}
        value={selectedDates}
        variant="filled"
        multiple
        sx={{
          marginTop: 'auto',
          paddingLeft: '0px !important',
          width: '133px',
          padding: '0px 8px 0px 12px',
          height: '40px',
          borderRadius: '8px',
          [`& .${selectClasses.multiple}`]: {
            background: 'transparent !important',
          },
        }}
        MenuProps={{
          classes: {
            paper: 'rounded-xl mt-2',
          },
        }}
        renderValue={(selected: InvoiceDate[]) => {
          const displayText = formatSelectedDatesDisplayWithMonthString(selected);
          const isMultipleSelection = displayText.startsWith('Multiple');
          const tooltipText = isMultipleSelection
            ? formatSelectedDatesTooltipWithMonthString(selected)
            : '';

          const content = <div className="flex gap-2 truncate text-[14px]">{displayText}</div>;

          if (isMultipleSelection) {
            return (
              <VyTooltip tooltipContent={tooltipText} placement="top" disableInteractive>
                {content}
              </VyTooltip>
            );
          }

          return content;
        }}
      >
        <div className="p-4">
          <div className="border-b-line mb-3 flex items-center justify-between border-b pb-3">
            <ArrowIcon
              onClick={() => handleYearChange(YEAR_CHANGE_TYPES.DECREMENT)}
              className="text-style cursor-pointer"
              direction={ARROW_ICON_DIRECTIONS.LEFT}
            />
            <button onClick={selectEntireYear}>{selectedYear}</button>
            <ArrowIcon
              onClick={() => handleYearChange(YEAR_CHANGE_TYPES.INCREMENT)}
              className="text-style cursor-pointer"
              direction={ARROW_ICON_DIRECTIONS.RIGHT}
            />
          </div>
          <div className="grid grid-cols-4 grid-rows-3 gap-2">
            {MONTHS_LABELS.map((month, index) => {
              const isSelected = isDateSelected(month, selectedYear, selectedDates);

              return (
                <div
                  key={index}
                  onClick={() => handleMonthSelect(month)}
                  className={classNames(
                    'flex cursor-pointer items-center justify-center rounded	border border-style px-4 py-2 text-style',
                    { 'bg-style text-white relative': isSelected },
                    { 'hover:border-black hover:text-black': !isSelected },
                  )}
                >
                  {isSelected && (
                    <div className="absolute -right-2.5 -top-1.5">
                      <SelectedIcon />
                    </div>
                  )}
                  <div>{month}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Select>
    </FormControl>
  );
};

const YEAR_CHANGE_TYPES = {
  INCREMENT: 'increment',
  DECREMENT: 'decrement',
} as const;

export type YearChangeType = ValueOf<typeof YEAR_CHANGE_TYPES>;
